import { Client } from 'src/api/Client';
import { InterceptableAxiosRequestConfig } from 'src/api/Http/Http.types';
import {
  DeleteLongTermMemoryResponse,
  ListLongTermMemoriesParams,
  LongTermMemory,
} from './LongTermMemoryClient.types';

/**
 * beN Brain long-term memories.
 *
 * be-core scopes these by the x-brand-id header (falling back to the user's own
 * id for a brand they can't act for), so every call names the brand the page is
 * showing rather than relying on the selected-brand cookie: the Brain page can
 * render a brand other than the selected one (customBrandId).
 *
 * Errors skip the global toast (`_noIntercept`): callers show their own, inline,
 * and a side section of the page must not raise a page-wide toast per retry.
 */
class LongTermMemoryClient extends Client {
  private readonly Route = {
    LIST: `${this.http.baseUrl}/ai-agent/long-term-memory`,
    ITEM: `${this.http.baseUrl}/ai-agent/long-term-memory/{id}`,
  };

  list(brandId: string, params: ListLongTermMemoriesParams = {}): Promise<LongTermMemory[]> {
    // Only defined params: the shared serializer sends undefined as an empty value.
    const query = Object.fromEntries(
      Object.entries(params).filter(([, value]) => value !== undefined && value !== ''),
    );
    const config: InterceptableAxiosRequestConfig = {
      params: query,
      headers: { 'x-brand-id': brandId },
      _noIntercept: true,
    };

    return this.http.get(this.Route.LIST, config) as unknown as Promise<LongTermMemory[]>;
  }

  delete(brandId: string, id: string): Promise<DeleteLongTermMemoryResponse> {
    const config: InterceptableAxiosRequestConfig = {
      headers: { 'x-brand-id': brandId },
      _noIntercept: true,
      // be-core finds the owner (up to two agent reads) before deleting; on a
      // merely slow agent that outlasts the default 20s and reports a delete
      // that then goes through as failed. Not a bound on a hung agent: the
      // load balancer cuts those first, and the awaited refetch shows the result.
      timeout: 90_000,
    };

    return this.http.delete(
      this.buildUrl(this.Route.ITEM, { id: encodeURIComponent(id) }),
      config,
    ) as unknown as Promise<DeleteLongTermMemoryResponse>;
  }
}

export { LongTermMemoryClient };
